/** EGB-00003 — Enterprise AI Governance Framework (mirrors pkg/enterpriseaigovernance) */

export const EGB_AI_ID = 'EGB-00003';

export interface CouncilMemberRow {
  role: string;
  responsibility: string;
  decisionRights: string;
  reviewCycle: string;
}

export interface PrincipleRow {
  principle: string;
  status: string;
  enforced: boolean;
}

export interface LifecycleRow {
  stage: string;
  status: string;
  count: number;
}

export interface PromptDNARow {
  category: string;
  name: string;
  version: string;
  status: string;
}

export interface AgentDecisionRow {
  area: string;
  detail: string;
  status: string;
}

export interface RiskRow {
  category: string;
  control: string;
  status: string;
  score: number;
}

export interface OversightSecurityRow {
  domain: string;
  policy: string;
  status: string;
}

export interface ComplianceMetricRow {
  category: string;
  item: string;
  value: string;
  status: string;
}

export const COUNCIL_ROLES = [
  'Chief AI Officer', 'Chief Enterprise Architect', 'Chief Product Officer', 'Security Architect',
  'Legal & Compliance Officer', 'Marketplace Specialist', 'AI Research Lead', 'Prompt Engineering Lead',
  'Data Governance Lead', 'Customer Success Lead', 'Accessibility Specialist',
];

export const COUNCIL_FUNCTIONS = ['Roles', 'Responsibilities', 'Decision Rights', 'Approval Matrix', 'Review Cycles', 'Escalation'];

export const AI_PRINCIPLES = [
  'Business First', 'Explainability', 'Transparency', 'Human Oversight', 'Accountability',
  'Security', 'Privacy', 'Marketplace Compliance', 'Continuous Learning',
];

export const LIFECYCLE_STAGES = [
  'Research', 'Prototype', 'Evaluation', 'Approval', 'Development', 'Testing', 'Certification',
  'Deployment', 'Monitoring', 'Learning', 'Retirement',
];

export const MODEL_GOVERNANCE = [
  'Approved Models', 'Versioning', 'Capabilities', 'Cost Profiles', 'Latency Profiles', 'Fallback Rules', 'Lifecycle', 'Retirement',
];

export const PROMPT_GOVERNANCE = [
  'Prompt Library', 'Templates', 'Versioning', 'Testing', 'Certification', 'Rollback', 'Ownership', 'Analytics',
];

export const DNA_LAYERS = [
  'Brand DNA', 'Product DNA', 'Customer Intent DNA', 'Growth DNA', 'Supply Chain DNA', 'CX DNA',
  'Financial DNA', 'Decision DNA', 'Organizational DNA', 'Knowledge DNA', 'Experience DNA',
];

export const AGENT_GOVERNANCE = [
  'Agent Registry', 'Capabilities', 'Permissions', 'Autonomy Levels', 'Knowledge Sources', 'Memory Policies', 'Approval Rules', 'Retirement',
];

export const DECISION_REQUIREMENTS = [
  'Business Goal', 'Reasoning', 'Confidence', 'Evidence', 'Prompt Version', 'Model Version', 'DNA Version', 'Business Impact', 'Outcome Tracking',
];

export const RESPONSIBLE_AI = [
  'Bias Detection', 'Fairness', 'Privacy', 'Hallucination Detection', 'Prompt Injection Protection',
  'Jailbreak Protection', 'Content Safety', 'Human Review',
];

export const AI_RISKS = [
  'Operational Risk', 'Legal Risk', 'Marketplace Risk', 'Brand Risk', 'Financial Risk', 'Security Risk', 'Reputation Risk',
];

export const HUMAN_OVERSIGHT = [
  'Suggestion Mode', 'Approval Mode', 'Co-Pilot Mode', 'Autonomous Mode', 'Emergency Stop', 'Manual Override',
];

export const AI_COST = ['Cost Per Prompt', 'Cost Per Workflow', 'Cost Per Customer', 'Budgets', 'Forecasts', 'Optimization', 'Alerts'];

export const AI_SECURITY = ['Prompts', 'Models', 'Credentials', 'Secrets', 'Sensitive Data', 'Tenant Isolation', 'Output Validation'];

export const AI_COMPLIANCE = ['GDPR', 'DPDP', 'SOC2 Readiness', 'Marketplace Policies', 'Privacy', 'Copyright', 'Retention', 'Deletion'];

export const AI_AUDIT = [
  'Prompt History', 'Response History', 'Decision Logs', 'Agent Activity', 'Automation Logs', 'Overrides', 'Incidents', 'Root Cause Analysis',
];

export const CONTINUOUS_LEARNING = ['Feedback', 'Business Outcomes', 'Prompt Refinement', 'DNA Evolution', 'Knowledge Updates', 'Model Evaluation'];

export const AI_METRICS = [
  'Acceptance Rate', 'Rejection Rate', 'Business Value', 'ROI', 'Confidence', 'Learning Rate', 'Automation Rate', 'Cost Efficiency',
];

export const AI_ETHICS = ['Transparency', 'Accountability', 'Responsible Automation', 'Experimental Models', 'Sandbox', 'A/B Testing', 'Research'];

export const ARCHITECTURE_STAGES = [
  'AI Governance Council', 'AI Principles', 'AI Lifecycle', 'Prompt & DNA Governance',
  'Agent & Decision Governance', 'Responsible AI & Risk', 'Human Oversight & Security', 'Compliance & Metrics',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  architectureGovernance: '/platform/architecture-governance',
  engineeringGovernance: '/platform/engineering-governance',
  governance: '/governance',
};

export const DEMO_COUNCIL: CouncilMemberRow[] = [
  { role: 'Chief AI Officer', responsibility: 'AI strategy & governance charter', decisionRights: 'Final AI capability approval', reviewCycle: 'Weekly' },
  { role: 'Prompt Engineering Lead', responsibility: 'Prompt library & certification', decisionRights: 'Prompt version approval', reviewCycle: 'Bi-weekly' },
  { role: 'AI Research Lead', responsibility: 'Research & experimental models', decisionRights: 'Sandbox access approval', reviewCycle: 'Monthly' },
  { role: 'Security Architect', responsibility: 'AI security & prompt injection defense', decisionRights: 'Security gate veto', reviewCycle: 'Weekly' },
  { role: 'Legal & Compliance Officer', responsibility: 'GDPR, DPDP, marketplace compliance', decisionRights: 'Compliance sign-off', reviewCycle: 'Monthly' },
  { role: 'Data Governance Lead', responsibility: 'DNA layers & data stewardship', decisionRights: 'DNA version approval', reviewCycle: 'Bi-weekly' },
];

export const DEMO_PRINCIPLES: PrincipleRow[] = [
  { principle: 'Business First', status: 'enforced', enforced: true },
  { principle: 'Explainability', status: 'enforced', enforced: true },
  { principle: 'Human Oversight', status: 'enforced', enforced: true },
  { principle: 'Accountability', status: 'enforced', enforced: true },
  { principle: 'Marketplace Compliance', status: 'enforced', enforced: true },
  { principle: 'Continuous Learning', status: 'active', enforced: true },
];

export const DEMO_LIFECYCLE: LifecycleRow[] = [
  { stage: 'Research', status: 'active', count: 8 },
  { stage: 'Prototype', status: 'active', count: 5 },
  { stage: 'Evaluation', status: 'in_review', count: 3 },
  { stage: 'Certification', status: 'passed', count: 12 },
  { stage: 'Deployment', status: 'production', count: 12 },
  { stage: 'Monitoring', status: 'active', count: 12 },
  { stage: 'Learning', status: 'active', count: 18 },
];

export const DEMO_PROMPT_DNA: PromptDNARow[] = [
  { category: 'Prompt', name: 'Listing Optimizer', version: 'v3.2', status: 'certified' },
  { category: 'Prompt', name: 'Growth Advisor', version: 'v2.8', status: 'certified' },
  { category: 'DNA', name: 'Brand DNA', version: 'v2.4', status: 'production' },
  { category: 'DNA', name: 'Product DNA', version: 'v1.8', status: 'production' },
  { category: 'DNA', name: 'Decision DNA', version: 'v1.2', status: 'production' },
  { category: 'Model', name: 'gpt-4o + fallback', version: 'current', status: 'approved' },
];

export const DEMO_AGENT_DECISION: AgentDecisionRow[] = [
  { area: 'Agent Registry', detail: '24 registered agents', status: 'governed' },
  { area: 'Autonomy', detail: 'L0–L5 levels enforced', status: 'enforced' },
  { area: 'Decision', detail: 'Confidence + reasoning required', status: 'mandatory' },
  { area: 'Decision', detail: 'Prompt/model/DNA version tracked', status: 'mandatory' },
  { area: 'Decision', detail: 'Business impact outcome tracking', status: 'active' },
];

export const DEMO_RISK: RiskRow[] = [
  { category: 'Operational Risk', control: 'Hallucination detection', status: 'active', score: 96 },
  { category: 'Legal Risk', control: 'GDPR + DPDP compliance', status: 'compliant', score: 98 },
  { category: 'Marketplace Risk', control: 'Amazon policy alignment', status: 'validated', score: 97 },
  { category: 'Brand Risk', control: 'Brand DNA governance', status: 'enforced', score: 99 },
  { category: 'Security Risk', control: 'Prompt injection protection', status: 'active', score: 95 },
  { category: 'Reputation Risk', control: 'Content safety filters', status: 'active', score: 94 },
];

export const DEMO_OVERSIGHT: OversightSecurityRow[] = [
  { domain: 'Human Oversight', policy: 'L4+ requires approval mode', status: 'enforced' },
  { domain: 'Human Oversight', policy: 'Emergency stop on all agents', status: 'available' },
  { domain: 'AI Cost', policy: '₹42K/mo budget with alerts', status: 'within_budget' },
  { domain: 'AI Security', policy: 'Tenant-isolated prompt execution', status: 'enforced' },
  { domain: 'AI Security', policy: 'Output validation on all responses', status: 'enforced' },
];

export const DEMO_COMPLIANCE: ComplianceMetricRow[] = [
  { category: 'Compliance', item: 'SOC 2 readiness', value: '85%', status: 'in_progress' },
  { category: 'Audit', item: 'Decision logs captured', value: '100%', status: 'compliant' },
  { category: 'Audit', item: 'Open AI incidents', value: '2', status: 'resolved' },
  { category: 'Metrics', item: 'Acceptance rate', value: '86%', status: 'healthy' },
  { category: 'Metrics', item: 'AI ROI', value: '5.2x', status: 'exceeded' },
  { category: 'Learning', item: 'Feedback loops active', value: '18.4K', status: 'growing' },
  { category: 'Ethics', item: 'Sandbox experiments', value: '4 active', status: 'governed' },
];

export function buildAIGovernanceDashboard() {
  return {
    council_roles: COUNCIL_ROLES.length,
    ai_principles: AI_PRINCIPLES.length,
    lifecycle_stages: LIFECYCLE_STAGES.length,
    dna_layers: DNA_LAYERS.length,
    risk_categories: AI_RISKS.length,
    ai_metrics: AI_METRICS.length,
  };
}
